import React from 'react';

type WatermarkProps = {
  className?: string;
  color?: string;
  opacity?: number;
};

/**
 * Filigrane "S" Gameside — motif décoratif vectoriel utilisé en arrière-plan
 * sur plusieurs sections. SVG inline pour zéro requête HTTP supplémentaire.
 */
export function WatermarkS({ className = '', color = '#ffffff', opacity = 0.04 }: WatermarkProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 1031.34 764.83"
      className={`pointer-events-none select-none ${className}`}
      style={{ opacity }}
      preserveAspectRatio="xMidYMid meet"
    >
      <path
        fill={color}
        d="M469.135 644.591l-401.98 34.862c-27.037 2.134-46.957 26.328-44.823 53.36l2.846 32.016 401.98-34.862c27.036-2.134 46.957-26.324 44.823-53.36zm131.622-525.776l379.924-33.434c27.036-2.134 46.957-26.324 44.823-53.36L1022.658.005 642.733 33.439c-27.035 2.134-46.957 26.324-44.822 53.36zm-80.4-74.7L224.39 69.724C89.21 81.824-11.11 200.634.99 335.813s130.911 235.5 266.09 223.4 235.5-130.911 223.4-266.09a245.926 245.926 0 00-76.84-157.942l64.744-5.692c27.036-2.134 46.957-26.324 44.823-53.36zm-260.4 434c-90.351 7.818-170.039-59.06-177.861-149.416s59.052-170.042 149.408-177.868 170.041 59.05 177.867 149.409-59.053 170.041-149.408 177.867zm770.521-23.478c-10.672-118.1-110.989-207.038-229.8-202.058l3.557 81.819a140.229 140.229 0 01145.14 128.065c6.4 76.839-50.515 145.14-127.353 151.543l-230.514 19.913c-27.036 2.134-46.957 26.324-44.823 53.36l2.846 32.016 279.607-24.19c121.662-10.672 212.018-118.815 201.346-240.476z"
      />
    </svg>
  );
}

/**
 * Ensemble de petites décorations (lightning, étoiles, points) à placer
 * dans les coins vides des sections pour casser la monotonie.
 */
type CornerDecorationProps = {
  className?: string;
  variant?: 'lightning' | 'stars' | 'dots' | 'plus';
  color?: string;
  opacity?: number;
};

export function CornerDecoration({
  className = '',
  variant = 'lightning',
  color = 'currentColor',
  opacity = 0.15,
}: CornerDecorationProps) {
  const common = `pointer-events-none select-none ${className}`;
  const style = { color, opacity };

  if (variant === 'lightning') {
    return (
      <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={common} style={style}>
        <path d="M13 2 3 14h7l-1 8 10-12h-7z" />
      </svg>
    );
  }

  if (variant === 'stars') {
    return (
      <svg aria-hidden="true" viewBox="0 0 64 64" fill="currentColor" className={common} style={style}>
        <path d="M14 6l1.5 4.5L20 12l-4.5 1.5L14 18l-1.5-4.5L8 12l4.5-1.5z" />
        <path d="M48 22l2 6 6 2-6 2-2 6-2-6-6-2 6-2z" />
        <path d="M30 44l1 3 3 1-3 1-1 3-1-3-3-1 3-1z" />
      </svg>
    );
  }

  if (variant === 'plus') {
    return (
      <svg aria-hidden="true" viewBox="0 0 64 64" fill="currentColor" className={common} style={style}>
        <path d="M14 6h4v6h6v4h-6v6h-4v-6H8v-4h6z" />
        <path d="M48 28h3v4.5h4.5v3H51V40h-3v-4.5h-4.5v-3H48z" />
      </svg>
    );
  }

  // dots
  return (
    <svg aria-hidden="true" viewBox="0 0 64 64" fill="currentColor" className={common} style={style}>
      <circle cx="6" cy="6" r="2" />
      <circle cx="18" cy="6" r="2" />
      <circle cx="30" cy="6" r="2" />
      <circle cx="6" cy="18" r="2" />
      <circle cx="18" cy="18" r="2" />
      <circle cx="6" cy="30" r="2" />
    </svg>
  );
}

/**
 * Filigrane micro / notes de musique pour la section karaoké.
 */
export function WatermarkMusic({ className = '', color = '#ffffff', opacity = 0.15 }: WatermarkProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 200 200"
      className={`pointer-events-none select-none ${className}`}
      style={{ opacity }}
      preserveAspectRatio="xMidYMid meet"
    >
      <g fill={color}>
        {/* Microphone */}
        <path d="M100 30c-12 0-22 10-22 22v40c0 12 10 22 22 22s22-10 22-22V52c0-12-10-22-22-22z" />
        <path d="M70 92c0 17 14 30 30 30s30-13 30-30h6c0 19-15 35-34 36v14h12v6H86v-6h12v-14c-19-1-34-17-34-36h6z" />
        {/* Note */}
        <circle cx="160" cy="160" r="12" />
        <path d="M170 160V100h-3v60h3z" />
        <path d="M167 100c8-2 18 0 22 5l-2 3c-3-3-12-5-20-3v-5z" />
      </g>
    </svg>
  );
}
